<template>
  <div
    :class="[
      'single-blog-post  mx-auto max-w-screen-xl',
      'slug-' + singleBlogPost?.blogPost?.slug,
      loading ? 'cursor-wait' : '',
    ]"
  >
    <OrganismsBWTextOverlay
      v-if="brandWindow"
      class="h-[500px]"
      :organism="brandWindow"
    />
    <div
      class="flex flex-col md:grid md:grid-cols-[242px_1fr] max-w-5xl mx-auto md:gap-16 pt-14 px-8"
    >
      <aside class="order-last sidebar md:order-none">
        <OrganismsSidebarBlog />
      </aside>
      <main class="max-w-full pb-20 overflow-hidden">
        <h2
          v-if="singleBlogPost?.blogPost?.customPostDetails.subtitle"
          class="mb-6 text-2xl font-semibold text-gray-900 subtitle font-archer md:text-5xl"
        >
          {{ singleBlogPost?.blogPost?.customPostDetails.subtitle }}
        </h2>
        <span class="mb-8 text-base font-bold date font-arial line-clamp-1">{{
          convertDateFormat(singleBlogPost?.blogPost?.date) ?? ""
        }}</span>
        <div
          v-if="loading"
          class="p-8 font-medium text-center text-gray-500 font-ubuntu"
        >
          Loading...
        </div>
        <div
          v-if="!loading"
          ref="blogContent"
          v-html="singleBlogPost?.blogPost?.content"
          class="text-lg text-gray-800 main-content mb-14"
        ></div>
        <MoleculesSocialShareLinks :molecule="socialShare" />
      </main>
    </div>
    <OrganismsFlexibleContentColumns
      id="three-column-contact"
      :organism="threeColumnContact"
    />
    <MoleculesLoadingOverlay v-if="loading" />
  </div>
</template>

<style>
.single-blog-post {
  .main-content {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      @apply font-archer text-2xl font-bold mb-8;
    }

    h5,
    h6 {
      @apply text-xl;
    }

    p {
      @apply mb-8;
    }

    ul {
      @apply list-disc ml-4 mb-8;

      li {
        @apply mb-1;
      }
    }

    a {
      @apply text-bc-dark-blue underline;
    }

    figure,
    img {
      @apply max-w-full max-h-[95vh];
    }

    figure {
      @apply mb-8;
    }

    figcaption {
      @apply text-gray-500 text-base text-center pt-1 italic;
    }

    img {
      &.alignleft {
        @apply float-left mr-8 mb-8;
      }
      &.aligncenter {
        @apply mx-auto block mb-8;
      }
      &.alignright {
        @apply float-right ml-8 mb-8;
      }
    }

    hr {
      @apply w-1/2 mb-8 bg-gray-500 mx-auto;
    }

    .wp-block-image {
      .alignleft {
        @apply float-left pr-[20px] w-1/2 mb-0;
      }

      .alignright {
        @apply float-right pl-[20px] w-1/2 mb-0;
      }
    }

    /* WordPress Gallery Block */
    .wp-block-gallery {
      display: flex;
      flex-wrap: wrap;
      gap: var(--wp--style--unstable-gallery-gap, 16px);
      list-style: none;
      padding: 0;
      margin-bottom: 2rem;
      max-height: none; /* Override parent figure 95vh max-height */

      /* Nested image figures */
      figure.wp-block-image {
        flex-grow: 1;
        flex-shrink: 0;
        margin: 0;
        position: relative;
        overflow: hidden;
        /* Default: 3 columns - use flex-basis (no max-width) so flex-grow expands last row */
        flex-basis: calc(33.333% - var(--wp--style--unstable-gallery-gap, 16px) * 0.667);

        img {
          display: block;
          width: 100%;
          max-height: none; /* Override parent 95vh max-height */
          height: auto;
          cursor: pointer;
        }

        /* Image caption overlay */
        figcaption {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
          color: #fff;
          text-align: center;
          font-size: 0.8125rem;
          padding: 8px;
          margin: 0;
          font-style: normal;
        }
      }

      /* Column variations (1-8) */
      &.columns-1 figure.wp-block-image {
        flex-basis: 100%;
      }
      &.columns-2 figure.wp-block-image {
        flex-basis: calc(50% - var(--wp--style--unstable-gallery-gap, 16px) * 0.5);
      }
      /* columns-3 and columns-default use the 33.333% default above */
      &.columns-4 figure.wp-block-image {
        flex-basis: calc(25% - var(--wp--style--unstable-gallery-gap, 16px) * 0.75);
      }
      &.columns-5 figure.wp-block-image {
        flex-basis: calc(20% - var(--wp--style--unstable-gallery-gap, 16px) * 0.8);
      }
      &.columns-6 figure.wp-block-image {
        flex-basis: calc(16.667% - var(--wp--style--unstable-gallery-gap, 16px) * 0.833);
      }
      &.columns-7 figure.wp-block-image {
        flex-basis: calc(14.285% - var(--wp--style--unstable-gallery-gap, 16px) * 0.857);
      }
      &.columns-8 figure.wp-block-image {
        flex-basis: calc(12.5% - var(--wp--style--unstable-gallery-gap, 16px) * 0.875);
      }

      /* Cropped mode - uniform image sizing via aspect-ratio */
      &.is-cropped figure.wp-block-image {
        img {
          aspect-ratio: 1;
          object-fit: cover;
          width: 100%;
          height: auto;
        }
      }

      /* Gallery-level caption */
      > .blocks-gallery-caption {
        flex-basis: 100%;
        text-align: center;
        color: #6b7280;
        font-size: 0.875rem;
        font-style: italic;
        margin-top: 0;
      }

      /* Responsive: single column on small screens */
      @media (max-width: 600px) {
        figure.wp-block-image {
          flex-basis: 100% !important;
          flex-grow: 0 !important;

          /* Remove forced square aspect-ratio at single column */
          img {
            aspect-ratio: auto !important;
            height: auto !important;
          }
        }
        &.columns-1 figure.wp-block-image {
          flex-basis: 100% !important;
        }
      }
    }
  }

  #three-column-contact.flexible-content-columns {
    @apply mb-20 pt-20 bg-gradient-to-b from-zinc-100 to-white;

    & > div {
      @apply border-2 border-gray-300 flex flex-col items-center justify-center p-10 gap-8 bg-white;
    }

    img {
      @apply w-20;
    }

    h3 {
      @apply font-archer font-semibold text-[28px] text-gray-700 leading-none;
    }

    .description {
      @apply text-lg text-gray-700 text-center;

      a {
        @apply underline text-bc-blue;
      }
    }
  }
}
</style>

<script setup>
const { path } = useRoute();
import { storeToRefs } from "pinia";
const blogPosts = useBlogPosts();
const { singleBlogPost, singleBlogPostLoading, singleBlogPostUri } =
  storeToRefs(blogPosts);

let loading = true;
const blogContent = ref(null);

watch(singleBlogPostLoading, (newLoading, oldLoading) => {
  loading = newLoading;
});

// Open gallery images in a new tab on click
const onGalleryClick = (e) => {
  const img = e.target.closest(".wp-block-gallery figure.wp-block-image img");
  if (!img) return;
  const fullSrc = img.src;
  if (fullSrc) window.open(fullSrc, "_blank");
};

watch(blogContent, (el) => {
  if (el) el.addEventListener("click", onGalleryClick);
});

onBeforeUnmount(() => {
  if (blogContent.value) blogContent.value.removeEventListener("click", onGalleryClick);
});

singleBlogPostUri.value = path;

const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
breadcrumbsList.value = [{ title: "Blog", link: "/blog/" }];

const threeColumnContact = {
  type: "flexible-content-columns",
  containerWidth: "full-width",
  item: [
    {
      backgroundColor: "#ffffff",
      button1: true,
      button1Color: "button-blue",
      button1Target: "_self",
      button1Text: "View All Homes",
      fieldGroupName: "item",
      link1: "/our-homes/quick-move-in-homes/",
      title1: "Explore Quick Move-In Homes",
      image: {
        altText: "",
        databaseId: 15340,
        id: "cG9zdDoxNTM0MA==",
        sourceUrl:
          "https://wp.livebouldercreek.com/wp-content/uploads/2023/04/icon-key@3x.png",
      },
    },
    {
      backgroundColor: "#ffffff",
      button1: true,
      button1Color: "button-blue",
      button1Target: "_self",
      button1Text: "View All Locations",
      description: "",
      fieldGroupName: "item",
      link1: "/contact-us/our-locations/",
      title1: "Find a Model Home Near You",
      image: {
        altText: "",
        databaseId: 15339,
        id: "cG9zdDoxNTMzOQ==",
        sourceUrl:
          "https://wp.livebouldercreek.com/wp-content/uploads/2023/04/icon-house@3x.png",
      },
    },
    {
      backgroundColor: "#ffffff",
      button1: true,
      button1Color: "button-blue",
      button1Target: "_self",
      button1Text: "View FAQ",
      description: "<p>We're here to help.</p>\n",
      fieldGroupName: "item",
      link1: "/contact-us/faq/",
      title1: "See Frequently Asked Questions",
      image: {
        altText: "question-mark icon",
        sourceUrl: "/img/question-mark.svg",
      },
    },
  ],
};

const brandWindow = computed(() => {
  const post = singleBlogPost?.value?.blogPost;
  if (!post) {
    return;
  }
  return {
    backgroundColor: "#ffffff",
    backgroundImageOpacity: 1,
    brandWindowLayout: "text-overlay-center",
    button1Color: "button-blue",
    button1Target: "_self",
    button2Color: "button-white",
    button2Target: "_self",
    button3Target: "_self",
    containerWidth: "full-width",
    contentTheme: "dark-text",
    title1: post.title,
    title2: "",
    type: "brand-window",
    backgroundImage: post.customPostDetails.image ?? {
      sourceUrl:
        "https://wp.livebouldercreek.com/wp-content/uploads/2022/08/BW_Calmante-Open-SpaceBiker_Lg-scaled.jpg",
    },
  };
});

const socialShare = computed(() => {
  const post = singleBlogPost?.value?.blogPost;

  const shareData = post
    ? {
        shareUrl: "https://livebouldercreek.com/" + path,
        title: post.title,
        imgSrc: post.customPostDetails.image?.sourceUrl,
      }
    : {
        shareUrl: "https://livebouldercreek.com/",
        title: "Visit Boulder Creek Neighorhoods",
        imgSrc:
          "https://wp.livebouldercreek.com/wp-content/uploads/2022/02/BW-Homepage-BW-slider_RF_ComingSoon_1-1024x459.jpg",
      };

  return shareData;
});
</script>
